import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IonContent, IonIcon, IonPage, IonRefresher, IonRefresherContent, IonSearchbar } from '@ionic/react';
import { swapVerticalOutline } from 'ionicons/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppHeader, StoreFilterBar, PeriodTabs, SegmentToggle, SuSelect, RankedTable, StoreName, StoreTypeLegend, YoYBadge } from '../components';
import type { Column } from '../components';
import type { Period, StoreRow } from '../api/types';
import { useStores } from '../hooks/useSuQueries';
import {
  selectFilters, setCities, setRegionMode, setRegions, setSort, SortKey, SuFiltersState,
} from '../state/filtersSlice';
import { formatMoney, moneyLabel } from '../utils/format';
import { PERIOD_LABELS } from '../utils/periods';
import { periodGroups, salesColumns } from './periodColumns';
import type { HeaderGroup } from '../components/RankedTable';
import { FILTERED_STORES_PATH, parseStoresQuery } from './stores/parseStoresQuery';
import { useLastRefreshed } from '../hooks/useLastRefreshed';
import { useAllStores, useRegionBreakdown } from './stores/useSuFetch';
import './stores/stores.css';

// Load every store in one request (API max is 200); there is no "View All" paging.
const PAGE_SIZE = 200;
const SKELETON_ROWS = 10;

const storeYoy = (v: number | null | undefined, isNew = false) => (
  <YoYBadge value={v ?? null} variant="inline" showArrow={false} isNew={isNew} />
);

// Compact on every tab (fits a ~360px phone): narrow numeric columns, unit stacked under the header.
// With a compare period each period gets its own Sales + YoY under a band (like State Wise); returns the bands too.
const storeColumns = (
  period: Period,
  comparePeriod: Period | null | undefined,
): { columns: Column<StoreRow>[]; groups?: HeaderGroup[] } => {
  const lead: Column<StoreRow>[] = [
    { key: 'rank', header: '#', width: 26, render: (r) => r.rank },
    { key: 'name', header: 'Store Name', render: (r) => <StoreName name={r.store_name} type={r.store_type} /> },
  ];
  if (comparePeriod) {
    const dual = periodGroups<StoreRow>(
      period, comparePeriod,
      { sales: (r) => r.sales, yoy: (r) => r.yoy_pct },
      { sales: (r) => r.compare_sales, yoy: (r) => r.compare_yoy_pct },
      56, 48, (v) => storeYoy(v),
    );
    return { columns: [...lead, ...dual.columns], groups: dual.groups };
  }
  return {
    columns: [
      ...lead,
      ...salesColumns<StoreRow>(period, null, (r) => r.sales, () => null, 48, true),
      {
        key: 'ly', header: <>LY Sales<br />{moneyLabel()}</>, align: 'right', width: 48,
        render: (r) => (r.ly_sales > 0 ? formatMoney(r.ly_sales) : '-'),
      },
      { key: 'yoy', header: 'YoY', align: 'right', width: 54, render: (r) => storeYoy(r.yoy_pct, r.store_type === 'NEW_STORE') },
    ],
  };
};

const SearchPanel: React.FC<{ period: SuFiltersState['period']; onClose: () => void; onPick: (code: string) => void }> = ({
  period, onClose, onPick,
}) => {
  const { data, loading, error, refetch } = useAllStores(period);
  const [q, setQ] = useState('');
  const barRef = useRef<HTMLIonSearchbarElement>(null);
  useEffect(() => {
    const t = setTimeout(() => barRef.current?.setFocus(), 150);
    return () => clearTimeout(t);
  }, []);
  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return (data?.items ?? []).filter(
      (s) => s.store_name.toLowerCase().includes(term) || s.site_code.toLowerCase().includes(term),
    );
  }, [data, q]);
  return (
    <div className="su-search">
      <IonSearchbar
        className="su-searchbar"
        ref={barRef}
        showCancelButton="always"
        placeholder="Search store name or code"
        value={q}
        onIonInput={(e) => setQ(String((e.target as HTMLIonSearchbarElement).value ?? ''))}
        onIonCancel={onClose}
      />
      {error && (
        <div className="su-muted">Could not load stores. <button type="button" className="su-link" onClick={refetch}>Retry</button></div>
      )}
      {loading && !data && <div className="su-skeleton" style={{ height: 44 }} />}
      {q.trim() && !loading && !error && results.length === 0 && <div className="su-muted">No stores found</div>}
      {results.map((s) => (
        <button key={s.site_code} type="button" className="su-search-item" onClick={() => onPick(s.site_code)}>
          <span className="grow"><StoreName name={s.store_name} type={s.store_type} /><small>{s.site_code} | {s.city}</small></span>
        </button>
      ))}
    </div>
  );
};

const StoresPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const f = useSelector((s: any) => selectFilters(s)) as SuFiltersState;
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [searchOpen, setSearchOpen] = useState(false);

  // Apply query params whenever this screen's URL changes (the outlet keeps it mounted).
  useEffect(() => {
    if (location.pathname !== '/app/stores' && location.pathname !== FILTERED_STORES_PATH) return;
    const q = parseStoresQuery(location.search);
    dispatch(setSort({ key: q.sort, order: q.order }));
    dispatch(setRegions(q.regions));
    dispatch(setCities(q.cities));
    dispatch(setRegionMode(q.regions.length || q.cities.length ? 'state' : 'all'));
    setLimit(PAGE_SIZE);
    if (q.focusSearch) setSearchOpen(true);
  }, [location.pathname, location.search, dispatch]);

  // Any filter/sort change starts from the first page again.
  const filterKey = JSON.stringify([f.period, f.storeType, f.regions, f.cities, f.sort]);
  useEffect(() => setLimit(PAGE_SIZE), [filterKey]);

  const stores = useStores({ limit, offset: 0 });
  const states = useRegionBreakdown('state', f.period, f.regionMode === 'state');
  const rows = stores.data?.items ?? null;
  const comparePeriod = stores.data?.compare_period ?? null;
  const table = useMemo(() => storeColumns(stores.data?.period ?? f.period, comparePeriod), [stores.data?.period, f.period, comparePeriod]);
  const total = stores.data?.total ?? 0;

  const stateOptions = states.data?.items ?? [];
  const selectedState = f.regions[0] ?? '';

  const onRefresh = (e: CustomEvent) => {
    stores.refetch();
    states.refetch();
    setTimeout(() => (e.target as any).complete(), 600);
  };

  // Back only when opened from a State > City drill-down (its own route), not from the Stores tab.
  const canGoBack = location.pathname === FILTERED_STORES_PATH;
  const lastRefreshed = useLastRefreshed();

  return (
    <IonPage>
      <AppHeader
        subtitle={lastRefreshed}
        onBack={canGoBack ? () => (history.action !== 'POP' ? history.goBack() : history.replace('/app/states')) : undefined}
        onSearch={() => setSearchOpen((o) => !o)}
      />
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={onRefresh}>
          <IonRefresherContent />
        </IonRefresher>
        <div className="su-page" style={{ paddingTop: 0 }}>
          <StoreFilterBar count={stores.data ? total : undefined} />
          <SegmentToggle
            variant="buttons"
            options={[{ value: 'all', label: 'All Network' }, { value: 'state', label: 'State' }]}
            value={f.regionMode}
            onChange={(m) => {
              dispatch(setRegionMode(m));
              if (m === 'all') {
                dispatch(setRegions([]));
                dispatch(setCities([]));
              }
            }}
          />
          {f.regionMode === 'state' && (
            <SuSelect
              ariaLabel="State"
              value={selectedState}
              fallbackLabel={selectedState}
              onChange={(v) => {
                dispatch(setRegions(v ? [v] : []));
                dispatch(setCities([]));
              }}
              options={[
                { value: '', label: 'All States' },
                ...stateOptions.map((s) => ({ value: s.key, label: `${s.state} (${s.store_count})` })),
              ]}
            />
          )}
          <PeriodTabs />

          {searchOpen && (
            <SearchPanel
              period={f.period}
              onClose={() => setSearchOpen(false)}
              onPick={(code) => history.push(`/app/store/${encodeURIComponent(code)}`)}
            />
          )}

          <div className="su-list-controls">
            <SuSelect
              ariaLabel="Sort by"
              value={f.sort.key}
              onChange={(v) => dispatch(setSort({ key: v as SortKey, order: f.sort.order }))}
              options={[
                { value: 'sales', label: `Sort by: Sales (${moneyLabel()})` },
                { value: 'yoy', label: 'Sort by: YoY %' },
              ]}
            />
            <button
              type="button"
              className="su-icon-btn"
              aria-label={f.sort.order === 'desc' ? 'Sorted descending' : 'Sorted ascending'}
              onClick={() => dispatch(setSort({ key: f.sort.key, order: f.sort.order === 'desc' ? 'asc' : 'desc' }))}
            >
              <IonIcon icon={swapVerticalOutline} />
            </button>
          </div>

          {stores.error && !rows ? (
            <div className="su-card su-error">
              <div>Could not load stores.</div>
              <button type="button" className="su-btn" onClick={stores.refetch}>Retry</button>
            </div>
          ) : (
            <div className="su-stores-table compact">
              <RankedTable
                legend={<StoreTypeLegend />}
                columns={table.columns}
                headerGroups={table.groups}
                footer={table.groups && `Sorted by ${f.sort.key === 'yoy' ? `${PERIOD_LABELS[stores.data?.period ?? f.period]} YoY` : `${PERIOD_LABELS[stores.data?.period ?? f.period]} sales`}. – means no figure available for that period.`}
                rows={rows}
                rowKey={(r) => r.site_code}
                loading={stores.loading}
                skeletonRows={SKELETON_ROWS}
                emptyText="No stores found"
                onRowClick={(r) => history.push(`/app/store/${encodeURIComponent(r.site_code)}`)}
              />
              {rows && (
                <div className="su-list-footer">
                  <span className="su-num">Showing {rows.length} of {total}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default StoresPage;
